import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { labelFor } from "@/lib/services";

const TEMPLATE = [
  "name,phone,email,zip_code,service_area_zips,city,state,services",
  "Jane Smith,(555) 123-4567,jane.smith@example.com,33101,\"33101, 33102\",Miami,FL,\"Blood, DOT urine, non-DOT hair, BAT DOT\"",
  "John Doe,,john.doe@example.com,,,,,\"non-DOT urine; DOT oral; non-DOT nails\"",
].join("\n");

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "collector-import-template.csv" });
  a.click();
  URL.revokeObjectURL(url);
};

const STATUS = {
  new: { label: "New profile + invite", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]" },
  join: { label: "Invite to join", cls: "bg-[#E9C46A]/30 text-[#8A5A00]" },
  error: { label: "Will be skipped", cls: "bg-[#E63946]/12 text-[#E63946]" },
};

const UploadStep = ({ onPreview }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/collectors/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onPreview(data, file.name);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-4" data-testid="import-upload-step">
      <div className="rounded-xl bg-[#F0EFEA] p-4 text-sm text-[#5C6B64] space-y-1.5">
        <p><span className="font-medium text-[#1E3F33]">Required:</span> name, plus phone or email (to send each invite).</p>
        <p><span className="font-medium text-[#1E3F33]">Optional:</span> zip_code, service_area_zips, city, state, services — the rest can be completed by you or the collector later.</p>
        <p><span className="font-medium text-[#1E3F33]">services:</span> plain names separated by commas or semicolons, e.g. "Blood, DOT urine, non-DOT hair, BAT DOT". Say DOT or non-DOT for urine, oral and BAT.</p>
        <p>Up to 200 collectors per file · CSV or Excel (.xlsx).</p>
      </div>
      <button type="button" onClick={downloadTemplate} data-testid="import-template-download" className="btn-secondary text-sm inline-flex items-center gap-2"><Download className="w-4 h-4"/> Download template</button>
      <input ref={ref} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="import-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
        data-testid="import-dropzone" className="w-full rounded-xl border-2 border-dashed border-[#1E3F33]/15 hover:border-[#2D6A4F]/50 p-8 text-center transition-colors">
        <FileSpreadsheet className="w-8 h-8 mx-auto text-[#2D6A4F]"/>
        <div className="text-sm font-medium text-[#1E3F33] mt-2">{busy ? "Checking your file…" : "Choose a file or drop it here"}</div>
        <div className="text-xs text-[#8A9A86] mt-1">.csv or .xlsx</div>
      </button>
    </div>
  );
};

const PreviewRow = ({ r }) => (
  <tr className="border-t border-[#1E3F33]/8 align-top" data-testid={`import-row-${r.row}`}>
    <td className="px-2 py-2 text-[#8A9A86]">{r.row}</td>
    <td className="px-2 py-2 text-[#1E3F33] font-medium">{r.name || "—"}</td>
    <td className="px-2 py-2 text-[#5C6B64] break-all min-w-[120px]">{[r.phone, r.email].filter(Boolean).join(" · ") || "—"}</td>
    <td className="px-2 py-2 text-[#5C6B64]">{[r.extras?.zip_code, r.extras?.city, r.extras?.state].filter(Boolean).join(", ") || "—"}</td>
    <td className="px-2 py-2 min-w-[140px]" data-testid={`import-row-services-${r.row}`}>
      {(r.extras?.services || []).length ? (
        <div className="flex flex-wrap gap-1">{r.extras.services.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F0EFEA] text-[#1E3F33] whitespace-nowrap">{labelFor(c)}</span>)}</div>
      ) : <span className="text-[#8A9A86]">—</span>}
    </td>
    <td className="px-2 py-2">
      <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS[r.status].cls}`} data-testid={`import-row-status-${r.row}`}>{STATUS[r.status].label}</span>
      {[...r.errors, ...r.warnings].map((m, i) => <div key={i} className={`text-[11px] mt-1 ${i < r.errors.length ? "text-[#E63946]" : "text-[#8A5A00]"}`}>{m}</div>)}
    </td>
  </tr>
);

const PreviewStep = ({ preview, fileName, onBack, onDone }) => {
  const [busy, setBusy] = useState(false);
  const { summary, billing, rows } = preview;
  const importable = summary.new + summary.join;
  const commit = async () => {
    setBusy(true);
    try {
      const payload = rows.filter((r) => r.status !== "error").map(({ row, name, phone, email, zip_code, service_areas, city, state, services }) => ({ row, name, phone, email, zip_code, service_areas, city, state, services: services || "" }));
      const { data } = await api.post("/company/collectors/import/commit", { rows: payload });
      onDone(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 min-w-0" data-testid="import-preview-step">
      <div className="text-xs text-[#8A9A86] inline-flex items-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5"/> {fileName} · {summary.total} rows</div>
      <div className="flex flex-wrap gap-2 text-xs" data-testid="import-summary">
        <span className="px-2.5 py-1 rounded-full bg-[#2D6A4F]/15 text-[#2D6A4F]">{summary.new} new</span>
        <span className="px-2.5 py-1 rounded-full bg-[#E9C46A]/30 text-[#8A5A00]">{summary.join} invite to join</span>
        <span className="px-2.5 py-1 rounded-full bg-[#E63946]/12 text-[#E63946]">{summary.error} with errors</span>
      </div>
      {billing.added_seats > 0 && (
        <p className="text-xs text-[#5C6B64]" data-testid="import-billing-note">Adds {billing.added_seats} collector{billing.added_seats === 1 ? "" : "s"} · +${billing.added_monthly.toFixed(2)}/mo (prorated today if your subscription is active) → new total ${billing.new_monthly_total.toFixed(2)}/mo. Existing collectors are only added once they accept.</p>
      )}
      <div className="max-h-[45vh] overflow-auto rounded-xl border border-[#1E3F33]/10">
        <table className="w-full text-xs" data-testid="import-preview-table">
          <thead className="bg-[#F0EFEA] text-left text-[#1E3F33] sticky top-0"><tr>
            <th className="px-2 py-2 font-medium">Row</th><th className="px-2 py-2 font-medium">Name</th><th className="px-2 py-2 font-medium">Contact</th><th className="px-2 py-2 font-medium">Location</th><th className="px-2 py-2 font-medium">Services</th><th className="px-2 py-2 font-medium">Result</th>
          </tr></thead>
          <tbody>{rows.map((r) => <PreviewRow key={r.row} r={r} />)}</tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2 justify-between">
        <button type="button" onClick={onBack} data-testid="import-back" className="btn-secondary text-sm inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4"/> Different file</button>
        <button type="button" disabled={busy || !importable} onClick={commit} data-testid="import-commit" className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"><Upload className="w-4 h-4"/> {busy ? "Importing…" : `Import ${importable} & send invites`}</button>
      </div>
    </div>
  );
};

const DoneStep = ({ result, onClose }) => (
  <div className="space-y-4" data-testid="import-done-step">
    <div className="flex items-start gap-2 text-sm text-[#2D6A4F]"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/>
      <span data-testid="import-done-counts">{result.counts.added} added, {result.counts.invited_to_join} invited to join, {result.counts.skipped} skipped. Invites are being sent now.</span>
    </div>
    <div className="flex items-start gap-2 text-xs text-[#8A5A00] bg-[#E9C46A]/15 rounded-xl p-3"><AlertTriangle className="w-4 h-4 shrink-0"/>
      New collectors won't receive leads until their profile is complete (services, service area and a certification). Services from your file are already filled in. You can finish each profile from Manage, or the collector can complete it after accepting their invite.
    </div>
    <button type="button" onClick={onClose} className="btn-primary text-sm" data-testid="import-done-close">Done</button>
  </div>
);

export const BulkImportDialog = ({ open, onOpenChange, onImported }) => {
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const close = (o) => { if (!o) { setPreview(null); setResult(null); } onOpenChange(o); };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl w-[calc(100vw-2rem)]" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1E3F33]">Import collectors from a spreadsheet</DialogTitle>
          <DialogDescription>Review every row before anything is created or sent.</DialogDescription>
        </DialogHeader>
        {result ? <DoneStep result={result} onClose={() => close(false)} />
          : preview ? <PreviewStep preview={preview} fileName={fileName} onBack={() => setPreview(null)} onDone={async (d) => { setResult(d); toast.success("Import complete"); await onImported(); }} />
          : <UploadStep onPreview={(d, n) => { setPreview(d); setFileName(n); }} />}
      </DialogContent>
    </Dialog>
  );
};
